// ─── Comptes bancaires (Firestore + calculs de solde) ─────────────
// Le COMPTE dit d'où vient l'argent (réalité bancaire) ; l'ENVELOPPE dit à quoi
// il sert (intention). Une opération porte les deux — ce sont deux questions
// différentes : « combien reste-t-il sur mon compte ? » et « ai-je tenu mon
// budget école ? ». Cf. CONCEPTION-FINANCE.md.
//
//   comptes/{id} { espaceId, nom, emoji, type, banque?, fin4?,
//                  soldeInitial, dateSolde, acctids: [], creePar, creeLe }
//   `acctids` : identifiants de compte lus dans les relevés OFX. Mémorisés au
//   premier import pour reconnaître le compte automatiquement ensuite.
import { db } from '@mop/core';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, where,
  getDocs, writeBatch, arrayUnion,
} from 'firebase/firestore';

export const TYPES_COMPTE = [
  { id: 'courant', emoji: '🏦' },
  { id: 'epargne', emoji: '🐖' },
  { id: 'credit',  emoji: '💳' },
  { id: 'autre',   emoji: '📁' },
];
export const emojiType = (id) => (TYPES_COMPTE.find(x => x.id === id) || TYPES_COMPTE[3]).emoji;
export const emojiCompte = (c) => (c && (c.emoji || emojiType(c.type))) || '🏦';

export function ecouterComptes(espaceId, callback, onErreur) {
  if (!espaceId) { callback([]); return () => {}; }
  const q = query(collection(db, 'comptes'), where('espaceId', '==', espaceId));
  return onSnapshot(q, (snap) => {
    const l = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    l.sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));
    callback(l);
  }, (e) => { callback([]); onErreur?.(e); });
}

const corps = (c) => ({
  nom: (c.nom || '').trim() || 'Compte',
  emoji: c.emoji || '',
  type: TYPES_COMPTE.some(x => x.id === c.type) ? c.type : 'courant',
  banque: (c.banque || '').trim(),
  fin4: (c.fin4 || '').replace(/\D/g, '').slice(-4),     // 4 derniers chiffres
  soldeInitial: Math.round((parseFloat(String(c.soldeInitial).replace(',', '.')) || 0) * 100) / 100,
  dateSolde: (c.dateSolde || '').slice(0, 10),
});

export async function creerCompte({ espaceId, creePar, ...c }) {
  const b = corps(c);
  if (!b.nom) throw new Error('Donne un nom au compte.');
  const ref = await addDoc(collection(db, 'comptes'), {
    espaceId, ...b, acctids: [], creePar: creePar || null, creeLe: new Date().toISOString(),
  });
  return ref.id;
}

export const modifierCompte = (id, c) => updateDoc(doc(db, 'comptes', id), corps(c));

// Mémorise l'identifiant de compte lu dans un relevé, pour le reconnaître aux
// imports suivants. `arrayUnion` : idempotent, et sans lecture préalable.
export const associerAcctid = (id, acctid) =>
  updateDoc(doc(db, 'comptes', id), { acctids: arrayUnion(String(acctid)) });

// Supprimer un compte NE SUPPRIME PAS ses opérations : elles perdent seulement
// leur rattachement. Détruire des mouvements bancaires en cascade serait une
// perte de données irréversible pour un simple changement de rangement.
export async function supprimerCompte(id, espaceId) {
  const snap = await getDocs(query(collection(db, 'depenses'), where('espaceId', '==', espaceId)));
  const refs = snap.docs.filter(d => d.data().compteId === id).map(d => d.ref);
  const LOT = 450;
  for (let i = 0; i < refs.length; i += LOT) {
    const batch = writeBatch(db);
    refs.slice(i, i + LOT).forEach(r => batch.update(r, { compteId: null }));
    await batch.commit();
  }
  await deleteDoc(doc(db, 'comptes', id));
}

// ── Solde ────────────────────────────────────────────────────────
// Le calcul vit désormais dans `releveCompte.js` : il était PUR mais coincé
// ici, derrière l'import Firestore de ce fichier — donc intestable. Ré-exporté
// tel quel, aucun appelant ne change.
export { soldeCompte, soldeCompteA, soldeFinDeMois } from './releveCompte.js';

// Compte reconnu à partir d'un identifiant de relevé (OFX).
export const compteParAcctid = (comptes = [], acctid) =>
  (acctid ? comptes.find(c => (c.acctids || []).includes(String(acctid))) : null) || null;
